import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation20() {
  const { lesson, course, accent } = findLesson("piano-foundation", "20");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>The 12-bar blues form is the spine. <strong>1-1-1-1 · 4-4-1-1 · 5-4-1-1</strong>. In C major: C-C-C-C / F-F-C-C / G-F-C-C.</Highlight>
        <Highlight>Lyrics follow an <strong>A-A'-B</strong> structure — line 1 sung, line 2 sung the same way, line 3 the punchline. "I'm tore down, almost level with the ground."</Highlight>
        <Highlight>Left-hand boogie: <strong>1-5-1-6</strong> on each chord. For C: C-G-C-A. For F: F-C-F-D. For G: G-D-G-E. That's a <strong>4:1 ratio</strong> — left hits four times under one right-hand chord.</Highlight>
        <Highlight>Right-hand <strong>Charleston rhythm</strong>: chord on beat 1 long, chord on the "and-of-2" short. Three sub-beats between strikes — the signature blues feel.</Highlight>
        <Highlight>Two scales for soloing: <strong>minor blues</strong> (C-E♭-F-G♭-G-B♭) and <strong>major blues</strong> (C-D-E♭-E-G-A). The "blue note" is the tritone — slide through it, never park on it.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Why blues" title="The genre that started everything else" accent={accent.hex}>
        <Pull>
          The blues is a very, very important contemporary genre. In fact, one might
          argue that it pretty much started everything off.
        </Pull>

        <p>
          The blues is the most useful single genre for a piano student because it covers
          everything in one — hand coordination, rhythm practice, scales, chords, swing
          feel, melodic improvisation. Drill the blues seriously and the rest of contemporary
          piano vocabulary opens up.
        </p>
      </Insight>

      <Insight eyebrow="The 12-bar form" title="1-1-1-1 · 4-4-1-1 · 5-4-1-1" accent={accent.hex}>
        <p>
          Twelve bars, three lines of four bars each, three chords (1, 4, 5). In C major:
        </p>

        <div className="rounded-xl border border-nsm-border bg-nsm-card/50 p-4 font-mono text-[14px]">
          <div className="flex gap-6">
            <span className="text-nsm-dim">Line 1 ·</span>
            <span className="text-emerald-300">C  ·  C  ·  C  ·  C</span>
          </div>
          <div className="mt-1 flex gap-6">
            <span className="text-nsm-dim">Line 2 ·</span>
            <span className="text-blue-300">F  ·  F  ·</span>
            <span className="text-emerald-300">C  ·  C</span>
          </div>
          <div className="mt-1 flex gap-6">
            <span className="text-nsm-dim">Line 3 ·</span>
            <span className="text-amber-300">G  ·</span>
            <span className="text-blue-300">F  ·</span>
            <span className="text-emerald-300">C  ·  C</span>
          </div>
        </div>

        <Vocab>
          <Term name="12-bar blues" def="the standard chord cycle for blues — twelve bars, three chords, repeated for the whole song." />
          <Term name="Turnaround" def="line 3 (G - F - C - C). The 'turn-around' that brings you back to the top of the next 12-bar cycle." />
          <Term name="A-A'-B" def="lyric structure. Line 1 = A. Line 2 = A again with slight variation. Line 3 = B, the punch-line." />
          <Term name="Swing feel" def="long-short-long-short subdivision instead of straight eighth notes. Defines the blues groove." />
        </Vocab>
      </Insight>

      <Insight eyebrow="The songs" title="Sing as you play — A-A'-B" accent={accent.hex}>
        <p>Two great songs to start with:</p>

        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 font-display text-[15px] text-amber-200">
          <strong>"I'm Tore Down"</strong><br />
          A — I'm tore down, almost level with the ground.<br />
          A' — Well I'm tore down, almost level with the ground.<br />
          B — Well I feel like this when my baby can't be found.
        </div>

        <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 font-display text-[15px] text-amber-200">
          <strong>"Sweet Home Chicago"</strong><br />
          Baby don't you wanna go… back to that same old place… sweet home Chicago.
        </div>

        <Tip>
          Listen to the originals. There are countless covers — pay homage to the artists
          who built the form. YouTube also has free "12-bar backing track in C" jam tracks;
          you can play along with a full band recorded for that exact purpose.
        </Tip>
      </Insight>

      <Insight eyebrow="The boogie bass" title="1 - 5 - 1 - 6 on every chord" accent={accent.hex}>
        <p>
          To make the form sound bluesy, the left hand stops just holding chords and starts
          playing a <strong>boogie line</strong>. The pattern: <strong>1 · 5 · 1 · 6</strong>{" "}
          across each bar — root, fifth, root, sixth.
        </p>

        <ul className="space-y-1 text-[14px]">
          <li>For <strong>C major</strong>: C - G - C - A</li>
          <li>For <strong>F major</strong>: F - C - F - D</li>
          <li>For <strong>G major</strong>: G - D - G - E</li>
        </ul>

        <p>
          The right hand can stay simple — just hold the chord while the left hand walks
          underneath. That's a <strong>4:1 ratio</strong> (four LH hits, one RH chord) — the
          mirror of the 1:4 ratio you used in earlier accompaniments.
        </p>

        <KeyIdea>
          As you get comfortable, start <em>swinging</em> the left hand — long-short-long-short
          on each pair of notes. That's the swing feel that makes the boogie sound like
          blues instead of like a march.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The Charleston rhythm" title="The right-hand groove of nearly every blues" accent={accent.hex}>
        <p>
          Once the LH boogie is running, the RH can graduate from "just hold the chord" to
          something properly bluesy: the <strong>Charleston rhythm</strong>. Two chord stabs
          per bar — the first on beat 1 (long), the second on the "and-of-2" (short).
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          1 · — · — · &-of-2 · — · — · 3 · 4
        </p>

        <p>
          Three sub-beats sit between each chord strike. Played at swing tempo, it's the most
          recognisable comping pattern in the blues. Almost every blues piano part uses it.
        </p>

        <Tip>
          The first hit is <strong>legato</strong> (let it ring); the second hit is{" "}
          <strong>staccato</strong> (clip it short). Same dynamics split as Oom-Pah in the
          previous lesson — long first, short second.
        </Tip>
      </Insight>

      <Insight eyebrow="Two blues scales" title="Minor blues for the main feel · major blues for sweetness" accent={accent.hex}>
        <p>
          For melodies and improvising over the 12-bar form, two scales:
        </p>

        <div className="space-y-2">
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3">
            <div className="text-xs font-bold uppercase tracking-wider text-rose-300">Minor blues (C)</div>
            <div className="mt-1 font-mono text-[14px]">C - E♭ - F - G♭ - G - B♭ - C</div>
            <div className="mt-1 text-[12px] text-nsm-dim">root · minor 3rd · 4th · ♭5 (the blue note) · 5th · ♭7 · octave</div>
          </div>
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-300">Major blues (C)</div>
            <div className="mt-1 font-mono text-[14px]">C - D - E♭ - E - G - A - C</div>
            <div className="mt-1 text-[12px] text-nsm-dim">root · 2nd · ♭3 · 3rd · 5th · 6th · octave</div>
          </div>
        </div>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C4", color: "#22c55e", label: "1" },
            { pitch: "Eb4", color: "#3b82f6", label: "♭3" },
            { pitch: "F4", color: "#3b82f6", label: "4" },
            { pitch: "Gb4", color: "#ef4444", label: "♭5 BLUE" },
            { pitch: "G4", color: "#3b82f6", label: "5" },
            { pitch: "Bb4", color: "#3b82f6", label: "♭7" },
            { pitch: "C5", color: "#22c55e", label: "8" },
          ]}
          height={180}
        />

        <Pull>
          You'll also observe when I play, my facial expressions change very, very
          drastically whenever I play the blues note, and I'm sure that will happen with
          you as well. I don't know why it happens, but yeah, it just happens.
        </Pull>

        <Tip>
          Use the blue note (G♭ in C) as a passing tone — slide through it on the way from
          F to G, or from G down to F. Don't park on it; the tension wants to resolve.
        </Tip>
      </Insight>

      <Insight eyebrow="Practice path" title="Build the blues a layer at a time" accent={accent.hex}>
        <Steps>
          <Step>
            Play the 12-bar form with both hands holding chords (1:4 ratio). Loop until the
            form feels familiar.
          </Step>
          <Step>
            Sing "I'm Tore Down" or "Sweet Home Chicago" over the chords. The 12-bar shape
            should now match the lyric pattern naturally.
          </Step>
          <Step>
            Add the boogie bass — 1-5-1-6 — to the LH on the C chord only. Eight bars of just
            C boogie. Then move it to F and G as the form requires.
          </Step>
          <Step>
            Add the Charleston rhythm in the RH while the LH keeps the boogie running.
            Long first hit, short second hit, on every bar.
          </Step>
          <Step>
            Solo over the form using the minor blues scale. Slide through the blue note —
            never sit on it. Add the major blues for variety once the minor feels home.
          </Step>
        </Steps>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-21"
        label="Lesson 21 · Zone Exercise"
        why="The blues opened up improvisation. The next exercise gives you a way to stay organised across the keyboard — three zones (low, mid, high) anchored by pivot notes."
      />
    </LessonShell>
  );
}
